import React from "react";
import { useTheme } from "../../themes/ThemeProvider";

/**
 * Theme toggle button for switching between light and dark mode.
 * Renders nothing when the active theme has no darkMode block
 * (keeps Intersum and Clean storefronts unaffected).
 *
 * Mounted inside the Header actions row next to Search/Cart.
 */
const ThemeToggle: React.FC = () => {
  const { themeMode, setThemeMode, supportsDarkMode } = useTheme();

  if (!supportsDarkMode) return null;

  const isDark = themeMode === "dark";
  const nextMode = isDark ? "light" : "dark";
  const label = isDark ? "Switch to light mode" : "Switch to dark mode";

  return (
    <button
      type="button"
      data-testid="theme-toggle"
      onClick={() => setThemeMode(nextMode)}
      aria-label={label}
      aria-pressed={isDark}
      title={label}
      className="text-text-secondary hover:text-primary transition-colors p-2 -m-2 rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
    >
      {isDark ? (
        // Sun icon — tapping will switch to light mode
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
      ) : (
        // Moon icon — tapping will switch to dark mode
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      )}
    </button>
  );
};

export default ThemeToggle;
